import { makeStyles } from '@material-ui/styles'
import GlobalDropdownWithSearch from 'core/elements/dropdown/GlobalDropdownWithSearch'
import React from 'react'

const useStyles = makeStyles((theme: any) => ({
  root: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridTemplateColumns: 'auto',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
  },
  filterDropdown: {
    width: 150,
    minWidth: 100,
    maxWidth: 200,
  },
}))

export interface FilterOption {
  value: string
  label: string
}

export interface FilterConfig {
  /** Unique key for this filter (used in API params) */
  key: string
  /** Display label for the filter */
  label: string
  /** Available options for this filter (not needed if using customComponent) */
  options?: FilterOption[]
  /** Default value (usually 'all') */
  defaultValue: string
  /** Custom component to render instead of default dropdown */
  customComponent?: React.ComponentType<CustomFilterComponentProps>
  /** Whether this filter should be visible (default: true) */
  visible?: boolean
}

export interface CustomFilterComponentProps {
  /** Current value of the filter */
  value: string
  /** Callback when value changes */
  onChange: (value: string) => void
  /** Whether the filter is disabled */
  disabled?: boolean
  /** CSS class name for styling */
  className?: string
}

export interface ServerSideFiltersProps {
  /** Filter configurations */
  filters: FilterConfig[]
  /** Current filter values */
  values: Record<string, string>
  /** Callback when a filter value changes */
  onChange: (key: string, value: string) => void
  /** Disabled state */
  disabled?: boolean
}

/**
 * Generic server-side filters component.
 *
 * Renders a row of dropdown filters based on the provided configuration.
 * Each filter has a key that maps to an API query parameter.
 */
export const ServerSideFilters: React.FC<ServerSideFiltersProps> = ({
  filters,
  values,
  onChange,
  disabled = false,
}) => {
  const classes = useStyles()

  // Filter out invisible filters. Keeping the check !== false for backward compatibility
  const visibleFilters = filters.filter((filter) => filter.visible !== false)

  return (
    <div className={classes.root}>
      {visibleFilters.map((filter) => {
        const currentValue = values[filter.key] ?? filter.defaultValue ?? 'all'

        // Render custom component if provided
        if (filter.customComponent) {
          const CustomComponent = filter.customComponent
          return (
            <CustomComponent
              key={filter.key}
              value={currentValue}
              onChange={(value) => onChange(filter.key, value)}
              disabled={disabled}
              className={classes.filterDropdown}
            />
          )
        }

        // Render default dropdown
        return (
          <GlobalDropdownWithSearch
            key={filter.key}
            label={filter.label}
            items={filter.options || []}
            onChange={(item) => onChange(filter.key, item.value)}
            value={currentValue}
            showLabelOnTop
            align="left"
            disabled={disabled}
          />
        )
      })}
    </div>
  )
}

export default ServerSideFilters
